/* KalaaDhara Bloom — motion layer. Everything here is decoration: if this file never loads,
   the store still works. The visual language is the studio's own process: things are
   "printed" in layer by layer, accents are "painted" in, and the cart is fed by filament.

   Motion is on when <html> has the class "motion" (set in layout/theme.liquid before first
   paint) and off with "no-motion". Visitors can switch it from the footer or the hero. */
(() => {
  'use strict';

  const B = (window.Bloom = window.Bloom || {});
  const root = document.documentElement;
  const $ = (sel, scope = document) => scope.querySelector(sel);
  const $$ = (sel, scope = document) => Array.from(scope.querySelectorAll(sel));
  const moving = () => root.classList.contains('motion');
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const editor = !!(window.Shopify && window.Shopify.designMode);
  const idle = (fn) => ('requestIdleCallback' in window ? window.requestIdleCallback(fn, { timeout: 1800 }) : setTimeout(fn, 250));
  const clamp = (x, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, x));

  /* ---------- Filament: scroll progress drawn as a strand of filament under the header ---------- */
  const initFilament = () => {
    const header = $('.hdr');
    if (!header || $('.filament', header)) return;
    const bar = document.createElement('div');
    bar.className = 'filament';
    bar.setAttribute('aria-hidden', 'true');
    bar.innerHTML = '<i></i><b></b>';
    header.appendChild(bar);
    let ticking = false;
    const paint = () => {
      ticking = false;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      bar.style.setProperty('--p', max > 40 ? clamp(window.scrollY / max).toFixed(4) : '0');
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(paint);
        }
      },
      { passive: true }
    );
    paint();
  };

  /* ---------- Print-in: pictures below the fold appear layer by layer; headings get inked ---------- */
  const PRINT = '.card__media, .tile__img, .promo__img, .story__media img, .post img';
  const INK = '.sec-head, .page-hero, .story__copy, .news, .coll-head, .ftr__mega';
  let revealer = null;
  const pending = new Map(); // watched parent -> pictures waiting to print
  const initReveals = (scope = document) => {
    if (!moving() || editor || !('IntersectionObserver' in window)) {
      $$(INK, scope).forEach((n) => n.classList.add('is-inked'));
      return;
    }
    revealer =
      revealer ||
      new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const el = entry.target;
            if (el.dataset.motion === 'ink') el.classList.add('is-inked');
            (pending.get(el) || []).forEach((n) => n.classList.add('is-printed'));
            pending.delete(el);
            revealer.unobserve(el);
          }
        },
        { rootMargin: '0px 0px -6% 0px', threshold: 0.12 }
      );
    $$(PRINT, scope).forEach((n) => {
      if (n.dataset.motion) return;
      n.dataset.motion = 'print';
      // Anything already on screen stays as it is: never hide what the shopper is looking at.
      const r = n.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0 && r.left < window.innerWidth) return;
      n.classList.add('will-print');
      const watch = n.parentElement;
      pending.set(watch, (pending.get(watch) || []).concat(n));
      revealer.observe(watch);
    });
    $$(INK, scope).forEach((n) => {
      if (n.dataset.motion || n.classList.contains('is-inked')) return;
      n.dataset.motion = 'ink';
      revealer.observe(n);
    });
  };
  const showEverything = () => {
    pending.clear();
    $$('.will-print').forEach((n) => n.classList.remove('will-print'));
    $$(INK).forEach((n) => n.classList.add('is-inked'));
  };
  window.addEventListener('beforeprint', showEverything);

  /* ---------- Tilt: cards lean towards the pointer, with a moving glare ---------- */
  const TILT = '.card, .tile, .promo, .quote, .step, .post, .pdp .gallery';
  const initTilt = () => {
    if (!fine) return;
    let current = null;
    const reset = (el) => {
      el.style.removeProperty('--rx');
      el.style.removeProperty('--ry');
      el.classList.remove('is-tilting');
    };
    document.addEventListener(
      'pointermove',
      (e) => {
        const el = moving() && e.target.closest ? e.target.closest(TILT) : null;
        if (current && current !== el) reset(current);
        current = el;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width, y = (e.clientY - r.top) / r.height;
        const max = el.classList.contains('gallery') ? 5 : 7;
        el.style.setProperty('--ry', ((x - 0.5) * 2 * max).toFixed(2) + 'deg');
        el.style.setProperty('--rx', ((0.5 - y) * 2 * max).toFixed(2) + 'deg');
        el.style.setProperty('--gx', (x * 100).toFixed(1) + '%');
        el.style.setProperty('--gy', (y * 100).toFixed(1) + '%');
        el.classList.add('is-tilting');
      },
      { passive: true }
    );
    document.documentElement.addEventListener('mouseleave', () => current && reset(current));
  };

  /* ---------- Phone tilt: on a product page the photo leans as the phone moves ---------- */
  const initGyro = () => {
    const gallery = $('.pdp .gallery');
    if (!gallery || fine || !window.DeviceOrientationEvent || typeof window.DeviceOrientationEvent.requestPermission === 'function') return;
    let base = null, rx = 0, ry = 0, tx = 0, ty = 0, raf = 0;
    const step = () => {
      raf = 0;
      rx += (tx - rx) * 0.12;
      ry += (ty - ry) * 0.12;
      gallery.style.setProperty('--rx', rx.toFixed(2) + 'deg');
      gallery.style.setProperty('--ry', ry.toFixed(2) + 'deg');
      if (Math.abs(tx - rx) + Math.abs(ty - ry) > 0.05) raf = requestAnimationFrame(step);
    };
    window.addEventListener(
      'deviceorientation',
      (e) => {
        if (e.beta == null || e.gamma == null) return;
        if (!moving()) {
          gallery.classList.remove('is-gyro');
          return;
        }
        if (!base) base = { beta: e.beta, gamma: e.gamma };
        // Drift the resting point slowly so any way of holding the phone feels level.
        base.beta += (e.beta - base.beta) * 0.01;
        base.gamma += (e.gamma - base.gamma) * 0.01;
        tx = clamp((base.beta - e.beta) / 4, -6, 6);
        ty = clamp((e.gamma - base.gamma) / 4, -6, 6);
        gallery.classList.add('is-gyro');
        if (!raf) raf = requestAnimationFrame(step);
      },
      { passive: true }
    );
  };

  /* ---------- Fly to cart: the product travels to the cart along a strand of filament ---------- */
  B.fly = (form) =>
    new Promise((resolve) => {
      const target = $('.hdr [data-cart-open]');
      if (!moving() || !target || form.closest('.drawer') || !Element.prototype.animate) return resolve();
      const card = form.closest('[data-product-card]');
      let src = card ? $('.card__img', card) : $('.gallery__slide img');
      let a = src ? src.getBoundingClientRect() : null;
      if (!a || a.bottom < 60 || a.top > window.innerHeight) {
        src = $('.sticky-atc.is-visible img') || src;
        a = ($('.sticky-atc.is-visible') || form).getBoundingClientRect();
      }
      if (!src) return resolve();
      const b = target.getBoundingClientRect();
      const size = clamp(Math.min(a.width, a.height), 56, 132);
      const from = { x: a.left + a.width / 2, y: a.top + a.height / 2 };
      const to = { x: b.left + b.width / 2, y: b.top + b.height / 2 };

      const layer = document.createElement('div');
      layer.className = 'fly';
      const ctrl = { x: (from.x + to.x) / 2 + (to.x - from.x) * 0.1, y: Math.min(from.y, to.y) - 90 };
      layer.innerHTML =
        `<svg width="100%" height="100%"><path d="M${from.x} ${from.y} Q${ctrl.x} ${ctrl.y} ${to.x} ${to.y}" pathLength="1"/></svg>` +
        `<span class="fly__x"><span class="fly__y" style="width:${size}px;height:${size}px;background-image:url('${src.currentSrc || src.src}')"></span></span>`;
      document.body.appendChild(layer);
      const xEl = $('.fly__x', layer), yEl = $('.fly__y', layer), path = $('path', layer);
      xEl.style.left = from.x - size / 2 + 'px';
      xEl.style.top = from.y - size / 2 + 'px';
      const dur = 620;
      path.animate([{ strokeDashoffset: 1, opacity: 1 }, { strokeDashoffset: 0, opacity: 1, offset: 0.7 }, { strokeDashoffset: 0, opacity: 0 }], { duration: dur + 120, easing: 'ease-out', fill: 'forwards' });
      xEl.animate([{ transform: 'translateX(0)' }, { transform: `translateX(${to.x - from.x}px)` }], { duration: dur, easing: 'cubic-bezier(.3,.1,.5,1)', fill: 'forwards' });
      const fly = yEl.animate(
        [
          { transform: 'translateY(0) scale(1) rotate(0deg)', borderRadius: '18px' },
          { transform: `translateY(${ctrl.y - from.y}px) scale(.62) rotate(-10deg)`, borderRadius: '40%', offset: 0.45 },
          { transform: `translateY(${to.y - from.y}px) scale(.16) rotate(18deg)`, borderRadius: '50%' },
        ],
        { duration: dur, easing: 'cubic-bezier(.4,0,.7,1)', fill: 'forwards' }
      );
      fly.onfinish = () => {
        layer.remove();
        target.classList.remove('is-bump');
        void target.offsetWidth;
        target.classList.add('is-bump');
        resolve();
      };
      setTimeout(resolve, dur + 400);
    });

  /* ---------- Confetti when free delivery unlocks ---------- */
  let hadFreeDelivery = null;
  const confetti = (host) => {
    if (!moving() || !Element.prototype.animate) return;
    const colours = ['#0e7c6b', '#ffb81c', '#d93a12', '#19b7cc', '#12233d'];
    const box = document.createElement('span');
    box.className = 'confetti';
    host.appendChild(box);
    for (let k = 0; k < 22; k++) {
      const bit = document.createElement('i');
      bit.style.background = colours[k % colours.length];
      box.appendChild(bit);
      const angle = (k / 22) * Math.PI * 2 + Math.random() * 0.4, dist = 60 + Math.random() * 90;
      bit.animate(
        [
          { transform: 'translate(0,0) rotate(0deg) scale(1)', opacity: 1 },
          { transform: `translate(${Math.cos(angle) * dist}px, ${Math.sin(angle) * dist * 0.7 - 30}px) rotate(${200 + Math.random() * 300}deg) scale(.6)`, opacity: 1, offset: 0.7 },
          { transform: `translate(${Math.cos(angle) * dist * 1.1}px, ${Math.sin(angle) * dist * 0.7 + 50}px) rotate(${500 + Math.random() * 300}deg) scale(.3)`, opacity: 0 },
        ],
        { duration: 900 + Math.random() * 500, easing: 'cubic-bezier(.2,.7,.3,1)', fill: 'forwards' }
      );
    }
    setTimeout(() => box.remove(), 1700);
  };
  const watchShipbar = () => {
    const bar = $('#CartDrawerInner .shipbar') || $('[data-cart-page] .shipbar');
    const done = !!(bar && bar.classList.contains('is-done'));
    if (hadFreeDelivery === false && done) confetti(bar);
    hadFreeDelivery = bar ? done : null;
  };

  /* ---------- Numbers count up when they scroll into view ---------- */
  let counter = null;
  const initCounters = (scope = document) => {
    if (!moving() || editor || !('IntersectionObserver' in window)) return;
    counter =
      counter ||
      new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          counter.unobserve(entry.target);
          const el = entry.target, text = el.textContent.trim();
          const match = text.match(/^(\d+(?:\.\d+)?)(.*)$/);
          if (!match) continue;
          const end = parseFloat(match[1]), decimals = (match[1].split('.')[1] || '').length, tail = match[2];
          const start = end > 1000 ? end - 16 : 0, t0 = performance.now(), dur = 1100;
          const tick = (now) => {
            const t = clamp((now - t0) / dur), eased = 1 - Math.pow(1 - t, 3);
            el.textContent = (start + (end - start) * eased).toFixed(decimals) + tail;
            if (t < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      });
    $$('.stat b', scope).forEach((n) => {
      if (n.dataset.counted) return;
      n.dataset.counted = '1';
      counter.observe(n);
    });
  };

  /* ---------- Page-to-page: the product photo you tap grows into the product page ---------- */
  const initViewTransitions = () => {
    if (!('CSSViewTransitionRule' in window)) return;
    document.addEventListener(
      'click',
      (e) => {
        const link = e.target.closest ? e.target.closest('a[href*="/products/"]') : null;
        if (!link || e.metaKey || e.ctrlKey || e.shiftKey) return;
        const scope = link.closest('[data-product-card], .hero__card, .pick, .upsell__item, .predictive__item') || link;
        const img = $('img', scope);
        $$('[data-vt]').forEach((n) => {
          n.style.viewTransitionName = '';
          n.removeAttribute('data-vt');
        });
        const main = $('.gallery__slide img');
        if (main) main.style.viewTransitionName = 'none';
        if (img) {
          img.style.viewTransitionName = 'product-media';
          img.dataset.vt = '1';
        }
      },
      true
    );
  };

  /* ---------- Live Print: the 3D piece in the hero ---------- */
  let idol = false; // true when a product mesh (the Krishna idol) is on the bed instead of the peacock
  const STATUS = {
    print: (p) => `Printing… ${Math.round(p * 100)}%`,
    scrub: (p) => `Layer view · ${Math.round(p * 100)}%`,
    lift: () => 'Print complete',
    paint: () => 'Painting by hand…',
    alive: () => (!moving() ? 'Printed & hand-painted' : idol ? 'Drag to turn · tap to spin' : 'Drag to spin · tap me'),
  };
  const loadScript = (src) =>
    new Promise((ok, fail) => {
      const el = document.createElement('script');
      el.src = src;
      el.onload = ok;
      el.onerror = fail;
      document.head.appendChild(el);
    });
  const loadImage = (src) =>
    new Promise((ok, fail) => {
      const im = new Image();
      im.crossOrigin = 'anonymous';
      im.onload = () => ok(im);
      im.onerror = fail;
      im.src = src;
    });
  const initLivePrint = () => {
    const host = $('[data-live-print]');
    if (!host || host.dataset.started) return;
    const fallback = () => root.classList.remove('can-3d');
    if (!root.classList.contains('can-3d') || editor) return fallback();
    host.dataset.started = '1';
    const wrap = host.parentElement;
    const status = $('[data-lp-status]', host), layer = $('[data-lp-layer]', host), scrub = $('[data-lp-scrub]', wrap);
    const steps = $$('[data-lp-step]', host);
    const order = ['print', 'paint', 'alive'];
    let lastLayer = -1, lastMode = '', scrubbing = false;

    const start = () => {
      // The engine, plus (for a product model) its mesh and two texture maps, all fetched together.
      const d = host.dataset;
      idol = Boolean(d.model);
      const jobs = [loadScript(d.src)];
      if (idol) jobs.push(loadScript(d.model), loadImage(d.albedo), loadImage(d.normal));
      Promise.all(jobs).then(([, , albedo, normal]) => {
        try {
          const model = idol ? { mesh: window.BloomModels[d.modelName], albedo, normal } : null;
          let seen = false;
          try {
            seen = sessionStorage.getItem('bloom-lp') === '1';
            sessionStorage.setItem('bloom-lp', '1');
          } catch (err) {
            /* private mode */
          }
          const engine = window.BloomLivePrint.mount(host, {
            ...(window.__bloomLpOpts || {}),
            model,
            fast: seen,
            calm: !moving(),
            onEvent: (name) => {
              if (name === 'lost' || name === 'slow') fallback();
            },
            onFrame: (eng) => {
              const s = eng.s;
              host.classList.add('is-ready');
              const total = window.BloomLivePrint.LAYERS, n = Math.round(s.print * total);
              if (n !== lastLayer || s.mode !== lastMode) {
                lastLayer = n;
                layer.textContent = `Layer ${n}/${total}`;
                status.textContent = (STATUS[s.mode] || STATUS.print)(s.print);
                if (!scrubbing) scrub.value = Math.round(s.print * 1000);
                scrub.style.setProperty('--v', s.print.toFixed(3));
              }
              if (s.mode !== lastMode) {
                lastMode = s.mode;
                host.dataset.mode = s.mode;
                const phase = s.mode === 'lift' || s.mode === 'scrub' ? 'print' : s.mode;
                const at = order.indexOf(phase);
                steps.forEach((li) => {
                  const k = order.indexOf(li.dataset.lpStep);
                  li.classList.toggle('is-on', k === at);
                  li.classList.toggle('is-done', k < at);
                });
              }
            },
          });
          scrub.addEventListener('input', () => {
            scrubbing = true;
            engine.scrub(scrub.value / 1000);
            engine.poke();
          });
          const release = () => {
            if (!scrubbing) return;
            scrubbing = false;
            if (moving()) engine.release();
            engine.poke();
          };
          scrub.addEventListener('change', release);
          scrub.addEventListener('pointerup', release);
          $('[data-lp-replay]', wrap).addEventListener('click', () => {
            engine.opts.calm = false;
            engine.replay();
            engine.poke();
            // With motion off the loop only runs while something is happening: keep it awake for the replay.
            if (!moving()) engine.active = performance.now() + 14000;
          });
          // The piece follows the mouse across the whole page, turns away as the hero scrolls out of
          // view (the peacock also folds its fan), and turns with the phone when it is tilted.
          let queued = false;
          const track = () => {
            queued = false;
            const r = host.getBoundingClientRect();
            engine.scrollOut = clamp(-r.top / (r.height * 0.85));
          };
          window.addEventListener(
            'scroll',
            () => {
              if (!queued) {
                queued = true;
                requestAnimationFrame(track);
              }
            },
            { passive: true }
          );
          if (fine) window.addEventListener('pointermove', (e) => moving() && engine.lookAtClient(e.clientX, e.clientY), { passive: true });
          if (window.DeviceOrientationEvent && typeof window.DeviceOrientationEvent.requestPermission !== 'function') {
            window.addEventListener(
              'deviceorientation',
              (e) => {
                if (e.gamma != null && moving()) engine.gyro = clamp(e.gamma / 38, -1, 1);
              },
              { passive: true }
            );
          }
          B.livePrint = engine;
        } catch (err) {
          fallback();
        }
      }, fallback);
    };
    // Never compete with the page load: wait for it, then for an idle moment.
    if (document.readyState === 'complete') idle(start);
    else window.addEventListener('load', () => idle(start), { once: true });
  };

  /* ---------- The motion switch ---------- */
  const syncToggles = () => {
    $$('[data-motion-toggle]').forEach((b) => {
      b.textContent = moving() ? 'Animations: on' : 'Animations: off';
      b.setAttribute('aria-pressed', String(moving()));
    });
  };
  const setMotion = (on) => {
    try {
      localStorage.setItem('bloom-motion', on ? 'on' : 'off');
    } catch (err) {
      /* private mode: the choice lasts for this page only */
    }
    root.classList.toggle('motion', on);
    root.classList.toggle('no-motion', !on);
    syncToggles();
    const engine = B.livePrint;
    if (on) {
      initReveals();
      initCounters();
      if (engine) {
        engine.opts.calm = false;
        engine.replay();
        engine.poke();
      }
    } else {
      showEverything();
      if (engine) {
        engine.opts.calm = true;
        engine.set({ mode: 'alive', print: 1, paint: 1, lift: 1, fan: 1, fanVel: 0, ripple: 0 });
        engine.poke();
      }
    }
  };
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-motion-on]')) setMotion(true);
    else if (e.target.closest('[data-motion-toggle]')) setMotion(!moving());
  });

  /* ---------- Boot ---------- */
  const boot = (scope = document) => {
    initReveals(scope);
    initCounters(scope);
  };
  const ready = () => {
    initFilament();
    boot();
    initTilt();
    initViewTransitions();
    initGyro();
    initLivePrint();
    watchShipbar();
    syncToggles();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready);
  else ready();
  document.addEventListener('bloom:cart', watchShipbar);
  document.addEventListener('bloom:content', (e) => boot(e.detail || document));
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();
